import { createDraftSafeSelector, createSlice, isPending, isRejected, PayloadAction } from "@reduxjs/toolkit";
import { JobItem, JobsResponse, ReprocessJobRequest } from "api/models/queues";
import { RootState } from "app";
import { getJobs, jobsActions, reprocessJob } from "./jobsActions";
import { getStuckJobsSummary, stuckJobsActions } from "./stuckJobsActions";

export type JobsState = {
    jobs: JobItem[];
    totalCount: number;
    sidePanelOpen: boolean;
    filters: {
        brandFamilyCode: string;
        status: number;
    };
    sorting: {
        orderBy: string;
        ascending: boolean;
    };
    pagination: {
        page: number;
        take: number;
    };
    loading: boolean;
    disableReprocessJobIds: string[];
    stuckJobs: {
        hasStuckJobs: boolean;
        stuckCount: number;
        loading: boolean;
        lastCheckDate: string;
    };
};

export const initialJobsState: JobsState = {
    jobs: [],
    totalCount: 0,
    sidePanelOpen: false,
    filters: {
        brandFamilyCode: "",
        status: 2 // Error status by default
    },
    sorting: {
        orderBy: "BrandFamilyCode",
        ascending: true
    },
    pagination: {
        page: 0,
        take: 10
    },
    loading: false,
    disableReprocessJobIds: [],
    stuckJobs: {
        hasStuckJobs: false,
        stuckCount: 0,
        loading: false,
        lastCheckDate: ""
    }
};

const jobsSlice = createSlice({
    name: "jobs",
    initialState: initialJobsState,
    reducers: {
        toggleSidePanel: (state) => {
            state.sidePanelOpen = !state.sidePanelOpen;
        },
        resetJobsPanelState: (state) => {
            state.sidePanelOpen = false;
            state.jobs = [];
            state.totalCount = 0;
            state.filters.brandFamilyCode = "";
            state.filters.status = 2;
            state.pagination.page = 0;
            state.disableReprocessJobIds = [];
        },
        openSidePanelWithFilters: (state, action: PayloadAction<{ brandFamilyCode: string; status: number }>) => {
            state.sidePanelOpen = true;
            state.filters.brandFamilyCode = action.payload.brandFamilyCode;
            state.filters.status = action.payload.status;
            state.pagination.page = 0;
        },
        setFilter: (state, action: PayloadAction<{ brandFamilyCode?: string; status?: number }>) => {
            if (action.payload.brandFamilyCode !== undefined)
                state.filters.brandFamilyCode = action.payload.brandFamilyCode;
            if (action.payload.status !== undefined)
                state.filters.status = action.payload.status;
        },
        setSorting: (state, action: PayloadAction<{ orderBy: string; ascending: boolean }>) => {
            state.sorting = action.payload;
            state.pagination.page = 0;
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.pagination.page = action.payload;
        },
        clearJobs: (state) => {
            state.jobs = [];
            state.totalCount = 0;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(
            getJobs.fulfilled,
            (state, action: PayloadAction<JobsResponse>) => {
                state.loading = false;
                if (action.payload?.success) {
                    state.jobs = action.payload.jobs;
                    state.totalCount = action.payload.totalCount;
                }
            }
        );
        builder.addCase(
            reprocessJob.fulfilled,
            (state, action) => {
                // Remove the job from the list if reprocess was successful
                if (action.payload?.success) {
                    const jobId = (action.meta.arg as ReprocessJobRequest).jobId;
                    state.jobs = state.jobs.filter(job => job.jobId !== jobId);
                    state.totalCount = Math.max(0, state.totalCount - 1);
                }
                state.disableReprocessJobIds = [];
            }
        );
        builder.addCase(
            getStuckJobsSummary.fulfilled,
            (state, action: PayloadAction<{ hasStuckJobs: boolean; stuckCount: number }>) => {
                state.stuckJobs.loading = false;
                if (action.payload) {
                    state.stuckJobs.hasStuckJobs = action.payload.hasStuckJobs;
                    state.stuckJobs.stuckCount = action.payload.stuckCount;
                    state.stuckJobs.lastCheckDate = new Date().toISOString();
                }
            }
        );
        builder.addMatcher(isPending, (state, { type, meta: { arg } }) => {
            switch (type) {
                case `${jobsActions.getJobs}/pending`:
                    state.loading = true;
                    break;
                case `${jobsActions.reprocessJob}/pending`:
                    const jobId = (arg as ReprocessJobRequest).jobId;
                    state.disableReprocessJobIds = [...state.disableReprocessJobIds, jobId];
                    break;
                case `${stuckJobsActions.getSummary}/pending`:
                    state.stuckJobs.loading = true;
                    break;
            }
        });
        builder.addMatcher(isRejected, (state, { type }) => {
            switch (type) {
                case `${jobsActions.getJobs}/rejected`:
                    state.loading = false;
                    break;
                case `${jobsActions.reprocessJob}/rejected`:
                    state.disableReprocessJobIds = [];
                    break;
                case `${stuckJobsActions.getSummary}/rejected`:
                    state.stuckJobs.loading = false;
                    break;
            }
        });
    }
});

const selectSelf = (state: RootState) => state.jobs;

export const selectJobs = createDraftSafeSelector(
    selectSelf,
    (state) => state.jobs
);

export const selectTotalCount = createDraftSafeSelector(
    selectSelf,
    (state) => state.totalCount
);

export const selectSidePanelOpen = createDraftSafeSelector(
    selectSelf,
    (state) => state.sidePanelOpen
);

export const selectJobsFilters = createDraftSafeSelector(
    selectSelf,
    (state) => state.filters
);

export const selectJobsSorting = createDraftSafeSelector(
    selectSelf,
    (state) => state.sorting
);

export const selectJobsPagination = createDraftSafeSelector(
    selectSelf,
    (state) => state.pagination
);

export const selectJobsLoading = createDraftSafeSelector(
    selectSelf,
    (state) => state.loading
);

export const selectDisableReprocessJobIds = createDraftSafeSelector(
    selectSelf,
    (state) => state.disableReprocessJobIds
);

export const selectHasStuckJobs = createDraftSafeSelector(
    selectSelf,
    (state) => state.stuckJobs.hasStuckJobs
);

export const selectStuckCount = createDraftSafeSelector(
    selectSelf,
    (state) => state.stuckJobs.stuckCount
);

export const selectStuckJobsLoading = createDraftSafeSelector(
    selectSelf,
    (state) => state.stuckJobs.loading
);

export const selectLastStuckCheck = createDraftSafeSelector(
    selectSelf,
    (state) => state.stuckJobs.lastCheckDate
);

export const {
    toggleSidePanel,
    resetJobsPanelState,
    openSidePanelWithFilters,
    setFilter,
    setSorting,
    setPage,
    clearJobs
} = jobsSlice.actions;
export default jobsSlice.reducer;